import { inject, injectable } from 'inversify';

import { TYPES } from '@/engine/ioc';

import type * as THREE from 'three';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { ICommand } from '@/engine/app/modules/core/interfaces/Command.ts';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm.ts';
import type { IMeasurementViewModel, IObjectViewModel } from '@/engine/app/interfaces.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type { CasePhaseData } from '@/engine/app/CasePhaseData/CasePhaseData.ts';

import { PointTuner } from '@/engine/PlanningAlgorithm/Tuners/PointTuner.ts';
import { InvertCommandDecorator } from '@/engine/app/modules/core/commands/Invert.command.ts';
import type { ITTVRVariableDistanceAlgoHelper } from '@/surgeries/cardiac/TTVR/interfaces.ts';
import { AddVariableDistanceCommand } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/commands/AddVariableDistance.command.ts';
import type { IVariableDistanceCommandsFactory } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/interfaces.ts';

@injectable()
export class VariableDistanceCommandsFactory implements IVariableDistanceCommandsFactory {
  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _viewsService: ViewsService;

  private readonly _casePhaseData: CasePhaseData;

  public constructor(
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData
  ) {
    this._planningAlgorithm = planningAlgorithm;
    this._viewsService = viewsService;
    this._casePhaseData = casePhaseData;
  }

  public createAddVariableDistanceCommand(
    view: ViewEntity,
    point: THREE.Vector3,
    pointViewModel: IObjectViewModel,
    distanceViewModel: IMeasurementViewModel,
    variableDistanceAlgorithmHelper: ITTVRVariableDistanceAlgoHelper
  ): ICommand {
    const pointTuner = new PointTuner(this._planningAlgorithm);
    return new AddVariableDistanceCommand(
      view,
      variableDistanceAlgorithmHelper,
      point,
      distanceViewModel,
      pointViewModel,
      pointTuner,
      this._viewsService,
      this._casePhaseData
    );
  }

  public createRemoveVariableDistanceCommand(
    view: ViewEntity,
    point: THREE.Vector3,
    pointViewModel: IObjectViewModel,
    distanceViewModel: IMeasurementViewModel,
    variableDistanceAlgorithmHelper: ITTVRVariableDistanceAlgoHelper
  ): ICommand {
    const addCommand = this.createAddVariableDistanceCommand(
      view,
      point,
      pointViewModel,
      distanceViewModel,
      variableDistanceAlgorithmHelper
    );
    return new InvertCommandDecorator(addCommand);
  }
}
